import { useState } from "react";
import { ApiClient } from "@/services/api";
import { Card, Button, Alert, LoadingSpinner } from "@/components/shared";
import { UnlockConfirmation } from "@/components/shared/UnlockConfirmation";
import { UpgradePrompt } from "@/components/shared/UpgradePrompt";

interface UnlockResult {
  unlocked?: boolean;
  requires_payment?: boolean;
  authorization_url?: string;
  already_unlocked?: boolean;
  contact?: {
    contactEmail: string | null;
    contactPhone: string | null;
    contactPreference: string;
  };
  expires_at?: string;
  selection_id?: string;
  intent_level?: string;
  unlock_fee?: string;
}

interface PricePreview {
  intent_level: string;
  price_cents: number;
  price_display: string;
  leads_remaining: number;
  can_unlock: boolean;
  block_reason: string | null;
}

export function QRScanner() {
  const [scanning, setScanning] = useState(false);
  const [manualToken, setManualToken] = useState("");
  const [result, setResult] = useState<UnlockResult | null>(null);
  const [pricePreview, setPricePreview] = useState<PricePreview | null>(null);
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const handleScan = async (token: string) => {
    setLoading(true);
    setError(null);
    setPricePreview(null);
    setShowUpgrade(false);

    try {
      const api = new ApiClient();

      // First, try to get the price preview if token contains a requestId
      // The token itself doesn't contain requestId directly, so we proceed with scan
      // and handle the pricing response
      const data = await api.post<UnlockResult>("/unlock/scan", { qr_token: token });

      if (data.requires_payment && data.authorization_url) {
        // Redirect to payment gateway
        window.location.href = data.authorization_url;
        return;
      }

      setResult(data);
      setScanning(false);
    } catch (err: any) {
      const errorCode = err.code || "";
      if (errorCode === "FREE_TIER_NO_UNLOCK" || errorCode === "LEAD_LIMIT_REACHED") {
        setShowUpgrade(true);
      } else {
        setError(err.message || "Failed to unlock. QR code may be expired or invalid.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (showUpgrade) {
    return (
      <Card>
        <UpgradePrompt
          currentTier="starter"
          leadLimit={0}
          variant="banner"
          onUpgrade={() => { window.location.href = "/agent/subscription"; }}
        />
      </Card>
    );
  }

  if (result) {
    return (
      <Card className="border-green-200 bg-green-50">
        <div className="text-center mb-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-green-900">Contact Unlocked!</h3>
        </div>

        <div className="space-y-3 bg-midnight rounded-lg p-4">
          {result.contact?.contactEmail && (
            <div>
              <p className="text-xs text-white/50">Email</p>
              <p className="font-medium text-white">{result.contact.contactEmail}</p>
            </div>
          )}
          {result.contact?.contactPhone && (
            <div>
              <p className="text-xs text-white/50">Phone</p>
              <p className="font-medium text-white">{result.contact.contactPhone}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-white/50">Preferred contact</p>
            <p className="font-medium text-white capitalize">{result.contact?.contactPreference}</p>
          </div>
        </div>

        <p className="text-xs text-white/50 mt-3 text-center">
          Access expires: {result.expires_at ? new Date(result.expires_at).toLocaleString("en-ZA") : "N/A"}
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <h3 className="font-semibold text-white mb-2">Scan QR Code</h3>
      <p className="text-sm text-white/60 mb-4">
        Scan the seller's QR code to unlock their contact details.
      </p>

      {error && <Alert type="error" className="mb-4">{error}</Alert>}

      {scanning ? (
        <div className="space-y-3">
          <div className="bg-white/5 border-2 border-dashed border-white/15 rounded-lg h-64 flex items-center justify-center">
            <p className="text-white/50 text-sm">Camera QR scanner placeholder</p>
            {/* In production: html5-qrcode scanner instance */}
          </div>
          <Button variant="secondary" onClick={() => setScanning(false)} className="w-full">
            Cancel Scan
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <Button onClick={() => setScanning(true)} variant="primary" className="w-full">
            Open Camera Scanner
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-midnight px-2 text-white/50">or enter token manually</span>
            </div>
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={manualToken}
              onChange={(e) => setManualToken(e.target.value)}
              placeholder="Paste QR token..."
              className="flex-1 input"
            />
            <Button
              onClick={() => handleScan(manualToken)}
              disabled={!manualToken || loading}
              variant="primary"
            >
              {loading ? <LoadingSpinner size="sm" /> : "Unlock"}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
